import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimAdditionalSupportNotice from './ClaimAdditionalSupportNotice'
import { ChevronRightIcon, FormCard, PrimaryButton } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherMaliciousDamageDescribeHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatIncidentReviewDateTime,
  formatIncidentReviewLocation,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import { buildFnolSubmitAgentEmailRows } from '../claims/agentEmailDetailRows'
import {
  formatOtherClaimSubtypeSummary,
  parseOtherClaimSubtypeQuery,
  type OtherClaimSubtypeId,
} from '../claims/otherClaimSubtypeOptions'
import {
  clearClaimDraft,
  getMultiVehicleDescribeParts,
  isMaliciousDamageDescribeGateSatisfied,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import ClaimAdditionalExcessesModal from './ClaimAdditionalExcessesModal'
import {
  ClaimReviewCollapsibleDescription,
  ClaimReviewSummaryRow,
} from './ClaimReviewSummaryRow'

import { formatSumInsuredSnippet } from '../claims/windscreenExcessUtils'
import { useApi } from '../services/api'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { isDdMmYyyyInLocalRange, PORTAL_DATE_MIN, startOfTodayLocal } from '../utils/dobDateInput'
import { isIncidentDdMmYyyyBeforePolicyStart } from '../utils/policyCoverStart'
function isValidAuPostcodeDigits(s: string): boolean {
  return /^\d{4}$/.test(s.trim())
}

const MALICIOUS_SUBTYPE = 'malicious-damage' as const satisfies OtherClaimSubtypeId

function buildMaliciousSearch(
  policyRef: string | null,
  typeInQuery: string | null,
  claimSubtype: OtherClaimSubtypeId
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (typeInQuery) p.set('type', typeInQuery)
  p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `?${q}` : ''
}

/**
 *  Malicious damage FNOL: confirm & submit.
 */
export default function ClaimOtherMaliciousDamageReviewStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { submitMaliciousDamageClaim } = useApi()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const policy = navState?.policy
  const draft = readClaimDraft()
  const { incident: reviewIncidentText, damage: reviewDamageText } = getMultiVehicleDescribeParts(draft)

const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [additionalExcessModalOpen, setAdditionalExcessModalOpen] = useState(false)
  const submitFeedbackRef = useRef<HTMLDivElement>(null)
  useScrollToTopOnErrorSignal(submitError)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimSubtype !== MALICIOUS_SUBTYPE) return
    const d = readClaimDraft()
    if (!isMaliciousDamageDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    }
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const maliciousSearch = claimSubtype !== null ? buildMaliciousSearch(policyRef, typeInQuery, claimSubtype) : ''

  const rego = policy?.registrationNumber?.trim() || policyRef || '—'
  const vehicleTitle =
    policy?.vehicleName?.trim() ||
    policy?.makeAndModel?.trim() ||
    [policy?.yearBuilt, policy?.make, policy?.model].filter(Boolean).join(' ') ||
    '—'

  const basicExcess =
    typeof policy?.basicExcess === 'number' && Number.isFinite(policy.basicExcess)
      ? `$${policy.basicExcess.toLocaleString('en-AU')}`
      : '$500'

  const backToDescribe = () => {
    if (submitting || claimSubtype === null) return
    navigate(buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, claimSubtype), {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const effectivePolicyRef = policyRef?.trim() || navState?.policy?.policyRef?.trim() || null

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitError(null)
    if (!effectivePolicyRef) {
      setSubmitError('Missing policy reference. Go back and choose your policy again.')
      return
    }
    const d = readClaimDraft()
    const { incident: incidentText, damage: damageText } = getMultiVehicleDescribeParts(d)
    if (!incidentText) {
      setSubmitError('Please add an incident description before submitting.')
      return
    }
    if (!damageText) {
      setSubmitError('Please describe the damage before submitting.')
      return
    }

    const incidentDate = d?.incidentDateDdMmYyyy?.trim() ?? ''
    if (!incidentDate || !isDdMmYyyyInLocalRange(incidentDate, PORTAL_DATE_MIN, startOfTodayLocal())) {
      setSubmitError(
        'When the incident occurred is missing or invalid. Use Back and complete the date and time step.'
      )
      return
    }
    if (isIncidentDdMmYyyyBeforePolicyStart(incidentDate, navState?.policy)) {
      setSubmitError(
        'Incident date cannot be before your policy start date. Use Back and correct the date on the when and where step.'
      )
      return
    }
    const incHm = d?.incidentTimeHm?.trim() ?? ''
    const incAp = d?.incidentTimeAmPm
    if (!/^\d{1,2}:\d{2}$/.test(incHm) || (incAp !== 'AM' && incAp !== 'PM')) {
      setSubmitError(
        'Incident time is missing or incomplete. Use Back and complete the date and time step.'
      )
      return
    }
    const addrUnk = d?.incidentAddressUnknown === true
    if (addrUnk) {
      if (!(d?.incidentState ?? '').trim()) {
        setSubmitError(
          'State is required for the incident location even when you do not know the exact address. Use Back and choose a state on the when and where step.'
        )
        return
      }
    } else if (
      !(d?.incidentStreet ?? '').trim() ||
      !(d?.incidentSuburb ?? '').trim() ||
      !isValidAuPostcodeDigits((d?.incidentPostcode ?? '').trim()) ||
      !(d?.incidentState ?? '').trim()
    ) {
      setSubmitError(
        'Street, suburb, state and postcode are required for the incident location. Use Back and complete that step.'
      )
      return
    }

    setSubmitting(true)
    try {
      const res = await submitMaliciousDamageClaim({
        policyRef: effectivePolicyRef,
        productRef: policy?.productRef ?? null,
        policyType: typeInQuery,
        description: damageText,
        lossDetailDescription: incidentText,
        requiresTowing: null,
        vehicleDrivable: true,
        incidentDateDdMmYyyy: incidentDate,
        incidentTimeHm: incHm,
        incidentTimeAmPm: incAp,
        incidentAddressUnknown: addrUnk,
        incidentAddressSearch: d?.incidentAddressSearch ?? null,
        incidentStreet: d?.incidentStreet ?? null,
        incidentSuburb: d?.incidentSuburb ?? null,
        incidentPostcode: d?.incidentPostcode ?? null,
        incidentState: d?.incidentState ?? null,
        agentEmailDetailRows: d
          ? buildFnolSubmitAgentEmailRows(d, 'malicious_damage', typeInQuery)
          : undefined,
      })
      if (!res || res.ok !== true) {
        setSubmitError(
          "Your claim wasn't submitted. Please try again, or call 1300 296 296 if the problem continues."
        )
        return
      }
      if (res.emailNotificationFailed) {
        setSubmitError(
          "Your claim notification has been lodged, but we couldn't display a claim number yet. Please call 1300 296 296 and we'll confirm it's on file. You do not need to submit the claim again."
        )
        return
      }
      clearClaimDraft()
      const submittedPath = `${CLAIM_FLOW_PATH.otherMaliciousDamageSubmitted}${maliciousSearch}`
      navigate(submittedPath, {
        state: {
          ...(navState?.policy ? { policy: navState.policy } : {}),
          maliciousDamageClaimSubmitted: true,
          claimNumber: res.claimNumber,
        },
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong. Please try again or call 1300 296 296.'
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const sumInsuredSnippet = formatSumInsuredSnippet(policy)

  const subtypeLabel = formatOtherClaimSubtypeSummary(MALICIOUS_SUBTYPE) ?? 'Malicious damage'

  if (claimSubtype === null || claimSubtype !== MALICIOUS_SUBTYPE) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToDescribe}
        backDisabled={submitting}
        title="Confirm your details"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
            weatherVehicleParkedAtIncident={draft?.weatherVehicleParkedAtIncident ?? null}
            fireVehicleParkedAtIncident={draft?.fireVehicleParkedAtIncident ?? null}
            theftVehicleParkedAtIncident={draft?.theftVehicleParkedAtIncident ?? null}
          />
        }
      >
        <FormCard>
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[4px]">
              <h2 className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
                Check the information below
              </h2>
              <p className="m-0 font-body text-sm leading-sm text-text-tertiary-600">
                Use the back button if you need to change any details.
              </p>
            </div>

            <section className="flex flex-col gap-[12px]" aria-labelledby="malicious-review-vehicle-heading">
              <h3
                id="malicious-review-vehicle-heading"
                className="m-0 font-body font-semibold text-md leading-md text-text-primary-900"
              >
                Vehicle
              </h3>
              <div className="rounded-xl border border-border-primary bg-background-secondary px-[14px] py-[14px]">
                <div className="content-start flex flex-wrap gap-[8px] items-start">
                  <span className="inline-flex items-center border border-text-primary-900 border-solid px-[8px] py-[4px] font-body font-medium text-md leading-md text-text-primary-900">
                    {rego}
                  </span>
                </div>
                <p className="mt-[12px] m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">
                  {vehicleTitle}
                </p>
              </div>
            </section>

            <section className="flex flex-col gap-[12px]" aria-labelledby="malicious-review-incident-heading">
              <h3
                id="malicious-review-incident-heading"
                className="m-0 font-body font-semibold text-md leading-md text-text-primary-900"
              >
                What happened
              </h3>
              <div className="rounded-xl border border-border-primary bg-background-primary px-[12px] sm:px-[16px]">
                <ClaimReviewSummaryRow label="Incident type">{subtypeLabel}</ClaimReviewSummaryRow>
                <ClaimReviewSummaryRow label="Date and time">{formatIncidentReviewDateTime(draft)}</ClaimReviewSummaryRow>
                <ClaimReviewSummaryRow label="Location">{formatIncidentReviewLocation(draft)}</ClaimReviewSummaryRow>
              </div>
            </section>
          </div>
        </FormCard>

        <FormCard>
          <div className="flex flex-col gap-[16px]">
            <h3 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">Further details</h3>
            <div className="rounded-xl border border-border-primary bg-background-primary px-[12px] sm:px-[16px]">
              <ClaimReviewSummaryRow label="Incident description">
                <ClaimReviewCollapsibleDescription text={reviewIncidentText} />
              </ClaimReviewSummaryRow>
              <ClaimReviewSummaryRow label="Damage details">
                <ClaimReviewCollapsibleDescription text={reviewDamageText} />
              </ClaimReviewSummaryRow>
            </div>
          </div>
        </FormCard>

        <FormCard>
          <div className="flex flex-col gap-[16px]">
            <h3 className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
              Before you submit
            </h3>
            <div>
              <p className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">Excesses</p>
              <ul className="mt-[10px] mb-0 list-disc space-y-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
                <li>
                  Your basic excess is <strong className="font-semibold text-text-primary-900">{basicExcess}</strong>
                </li>
                <li>In some cases there may be additional excess — we&apos;ll confirm when we contact you.</li>
              </ul>
              <button
                type="button"
                className="mt-3 self-start border-0 bg-transparent p-0 font-body text-sm font-semibold text-background-brand-solid underline decoration-solid underline-offset-2 hover:text-text-brand-secondary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2"
                onClick={() => setAdditionalExcessModalOpen(true)}
              >
                View additional excesses that may apply
              </button>
            </div>
          </div>
        </FormCard>

        <ClaimAdditionalSupportNotice />

        <div className="sticky bottom-0 z-10 w-full border-t border-border-secondary bg-background-secondary pt-[12px] pb-[8px] md:static md:z-auto md:border-t-0 md:py-0">
          <div ref={submitFeedbackRef} className="mb-[12px] flex flex-col gap-[8px]" aria-live="polite">
            {submitting ? (
              <p className="m-0 font-body text-sm font-semibold text-text-secondary-700">
                Sending your claim… This can take up to a minute.
              </p>
            ) : null}
            {submitError ? (
              <p
                className="m-0 rounded-lg border border-red-200 bg-red-50 px-[14px] py-[12px] font-body text-sm text-red-900"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}
          </div>
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            onClick={() => void handleSubmit()}
            disabled={submitting}
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label={submitting ? 'Submitting' : 'Submit claim'}
          >
            {submitting ? 'Submitting…' : 'Submit claim'}
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />

      <ClaimAdditionalExcessesModal
        open={additionalExcessModalOpen}
        onClose={() => setAdditionalExcessModalOpen(false)}
        sumInsuredSnippet={sumInsuredSnippet}
      />
    </div>
  )
}
